<template>
    <div class="SignupPage">
        <SiteHeader/>

        <Container v-if="stage === 'estimate'">
            <h1>30 Day Free Trial</h1>
            <Card>
                <form @submit.prevent="">
                    <SitePlanEstimator :cost="planCost" :drivers.sync="drivers" />
                    <Button class="primary block" type="submit" @click="stage = 'card'">Next</Button>
                </form>
            </Card>
        </Container>

        <Container v-if="stage === 'card'" class="billingDetails">
            <div class="header">
                <ButtonIcon icon="chevron-left" @click="stage = 'estimate'">Go back</ButtonIcon>
                <h1>Billing details</h1>
            </div>

            <Card>
                <div class="statement">
                    <h4>DumpTek - {{ drivers }} drivers</h4>
                    <div class="price">Total <strong>${{ drivers * planCost }}/mo</strong></div>
                </div>

                <div class="divider"/>

                <CardForm
                    ref="cardForm"
                    :firstName.sync="firstName"
                    :lastName.sync="lastName"
                    :address.sync="address"
                    :city.sync="city"
                    :state.sync="state"
                    :zip.sync="zip"
                />

                <Button
                    class="primary block"
                    type="submit"
                    @click="handleSubmit"
                    :disabled="submitStatus.startsWith('PENDING')"
                >
                    <span v-if="submitStatus === 'PENDING_CUSTOMER'">Creating Customer...</span>
                    <span v-else-if="submitStatus === 'PENDING_PAYMENT'">Creating Payment Method...</span>
                    <span v-else-if="submitStatus === 'PENDING_SUBSCRIPTION'">Creating Subscription...</span>
                    <span v-else>Start 30 Day Free Trial</span>
                </Button>

                <p class="notice">${{ drivers * planCost }}/month (plus any applicable taxes). Cancel anytime.</p>
            </Card>
        </Container>

        <SiteFooter/>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import * as api from '@/api';
import Card from '@/components/Card';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import SitePlanEstimator from '../components/SitePlanEstimator';
import CardForm from '@/components/form/CardForm';
import { getStripeProduct } from '@/utils/getStripeProduct';

export default {
    name: 'SignupSubscriptionPage',

    components: {
        SiteHeader,
        SiteFooter,
        Card,
        CardForm,
        SitePlanEstimator,
    },

    data: () => ({
        stage: 'estimate', // estimate, card

        drivers: 5,

        firstName: '',
        lastName: '',
        address: '',
        city: '',
        state: '',
        zip: '',

        submitStatus: '',
    }),

    async mounted() {
        await this.$store.dispatch('company/fetchOverview');

        if (this.user?.stripeCustomerId && this.subscription) {
            return this.$router.push('/company/dashboard');
        }
    },

    computed: {
        ...mapState('auth', ['user']),
        ...mapState('company', ['subscription']),
        ...mapGetters('auth', ['company']),

        planCost() {
            const planPrice = getStripeProduct(this.company.id);
            return planPrice;
        },
    },

    methods: {
        async handleSubmit() {
            if (!this.$refs.cardForm.validate()) {
                return;
            }

            this.submitStatus = 'PENDING';

            if (!this.user.stripeCustomerId) {
                this.submitStatus = 'PENDING_CUSTOMER';
                const res = await api.createCustomer();
                if (!res.ok) {
                    this.submitStatus = 'ERROR';
                    return this.$awn.alert('Failed to create stripe customer');
                }

                await this.$store.dispatch('auth/refreshUser');
            }

            this.submitStatus = 'PENDING_PAYMENT';
            const stripeRes = await this.$stripe.createPaymentMethod({
                type: 'card',
                card: this.$refs.cardForm.card,
                billing_details: {
                    email: this.user.email,
                    name: `${this.firstName} ${this.lastName}`.trim(),
                    address: {
                        country: 'US',
                        state: this.state,
                        city: this.city,
                        line1: this.address,
                        postal_code: this.zip,
                    },
                },
            });

            if (stripeRes.error) {
                this.submitStatus = 'ERROR';
                return this.$awn.alert(stripeRes.error.message);
            }

            const pmRes = await api.createPaymentMethod(stripeRes.paymentMethod.id);
            if (!pmRes.ok) {
                this.submitStatus = 'ERROR';
                return this.$awn.alert('Failed to attach payment method to customer');
            }

            this.submitStatus = 'PENDING_SUBSCRIPTION';
            const subRes = await api.createSubscription({ plan: process.env.VUE_APP_STRIPE_DEFAULT_PLAN_ID, quantity: Number(this.drivers) });
            if (!subRes.ok) {
                this.submitStatus = 'ERROR';
                return this.$awn.alert('Failed to create subscription');
            }

            this.submitStatus = 'OK';
            this.$router.push('/signup/subscription-finalize');
        },
    },
}
</script>

<style lang="scss" scoped>
@import 'globals.scss';

.SignupPage {
    display: flex;
    flex-wrap: wrap;
    min-height: 100vh;

    .Container {
        margin-top: 30px;
        margin-bottom: 80px;
        text-align: center;

        h1 {
            @extend %display-subtitle;
            margin-bottom: 30px;
        }
    }

    .Card {
        max-width: 600px;
        margin: 0 auto;
    }

    .SitePlanEstimator {
        margin-bottom: 30px;
    }

    .SiteFooter {
        align-self: flex-end;
    }

    .billingDetails {
        margin-top: 60px;
    }

    .header {
        display: flex;
        align-items: center;
        margin-bottom: 30px;
        position: relative;

        h1 {
            @include absolute-center();
        }
    }

    .info {
        margin-top: 40px;
    }

    .statement {
        margin-bottom: 30px;
        text-align: left;

        .price {
            font-size: 28px;
            font-weight: $font-weight-regular;
        }
    }

    .divider {
        border-top: 1px solid $divider-color;
        margin: 10px 0 30px;
    }

    .notice {
        padding-top: 15px;
    }
}
</style>
@/utils/getStripeProduct